import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { motion, AnimatePresence } from 'framer-motion';
import { useHapticFeedback } from '@/hooks/useHapticFeedback';
import { useStore } from '@/store/useStore';
import { usernameMarketApi } from '@/utils/api';
import { v2Api } from '@/utils/v2Api';
import { socket } from '@/utils/socket';
import { BalanceBar } from '@/components/ui/BalanceBar';
import { ExperimentalFilterBar } from '@/components/ui/ExperimentalFilterBar';
import { CustomEmojiPlayer } from '@/components/ui/CustomEmojiPlayer';
import { GramIcon } from '@/components/ui/GramIcon';
import { PushMarketModal } from '@/components/Modals/PushMarketModal';
import {
    Users, Handshake, Activity, Plus, ArrowUp, Send, ShoppingCart, Phone,
    Pencil, FileX, XCircle, Edit3, AtSign, Gem, ShoppingBag, Gavel, LayoutGrid,
    Check, X, CornerDownLeft, Sparkles, RefreshCw, Archive, UserRound, ChevronRight, Trash2
} from 'lucide-react';

// ─── EXACT ACTION BAR SVGS FROM DEV.PORTALS-MARKET.COM ─── //
const SvgPlusAction = (props: React.SVGProps<SVGSVGElement>) => (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" {...props}>
        <path fillRule="evenodd" clipRule="evenodd" d="M13.7143 1.71429C13.7143 0.767511 12.9468 0 12 0C11.0532 0 10.2857 0.767511 10.2857 1.71429V10.2857H1.71429C0.767511 10.2857 0 11.0532 0 12C0 12.9468 0.767511 13.7143 1.71429 13.7143H10.2857V22.2857C10.2857 23.2325 11.0532 24 12 24C12.9468 24 13.7143 23.2325 13.7143 22.2857V13.7143H22.2857C23.2325 13.7143 24 12.9468 24 12C24 11.0532 23.2325 10.2857 22.2857 10.2857H13.7143V1.71429Z" fill="currentColor" />
    </svg>
);

const SvgAnchorArrowAction = (props: React.SVGProps<SVGSVGElement>) => (
    <svg width="16" height="16" viewBox="0 0 18 18" fill="none" xmlns="http://www.w3.org/2000/svg" {...props}>
        <path fillRule="evenodd" clipRule="evenodd" d="M8.54549 0.188289C8.79654 -0.0627629 9.20357 -0.0627629 9.45462 0.188289L13.9547 4.68829C14.1385 4.87215 14.1934 5.14864 14.0939 5.38887C13.9944 5.62909 13.76 5.78571 13.5001 5.78571H10.2858V16.7143C10.2858 17.4244 9.71013 18 9.00005 18C8.28997 18 7.71434 17.4244 7.71434 16.7143V5.78571H4.50005C4.24004 5.78571 4.00563 5.62909 3.90613 5.38887C3.80663 5.14864 3.86163 4.87215 4.04549 4.68829L8.54549 0.188289Z" fill="currentColor" />
    </svg>
);

const SvgSendGiftAction = (props: React.SVGProps<SVGSVGElement>) => (
    <svg width="16" height="16" viewBox="0 0 17 16" fill="none" xmlns="http://www.w3.org/2000/svg" {...props}>
        <path fillRule="evenodd" clipRule="evenodd" d="M10.4454 0.251051C10.1106 -0.0836838 9.56793 -0.0836838 9.23319 0.251051C8.89846 0.585786 8.89846 1.1285 9.23319 1.46323L10.0557 2.28571H7.55357C7.23798 2.28571 6.98214 2.54155 6.98214 2.85714V8.57143C6.98214 8.88702 7.23798 9.14286 7.55357 9.14286H10.8393V4C10.8393 3.60551 11.1591 3.28571 11.5536 3.28571C11.9481 3.28571 12.2679 3.60551 12.2679 4V9.14286H15.5536C15.8691 9.14286 16.125 8.88702 16.125 8.57143V2.85714C16.125 2.54155 15.8691 2.28571 15.5536 2.28571H13.0515L13.8739 1.46323C14.2087 1.1285 14.2087 0.585786 13.8739 0.251051C13.5392 -0.0836838 12.9965 -0.0836838 12.6618 0.251051L11.5536 1.35925L10.4454 0.251051ZM2.23146 8H0.125V12.5714L2.21463 14.661C3.07194 15.5184 4.2347 16 5.44711 16H12.125C13.0717 16 13.8393 15.2325 13.8393 14.2857C13.8393 13.339 13.0717 12.5714 12.125 12.5714H8.95161C8.84134 12.9447 8.6389 13.2966 8.34429 13.5912C7.40261 14.5329 5.87585 14.5329 4.93417 13.5912L3.27701 11.9341C2.99806 11.6551 2.99806 11.2029 3.27701 10.9239C3.55596 10.645 4.00822 10.645 4.28716 10.9239L5.94433 12.581C6.32811 12.9649 6.95035 12.9649 7.33414 12.581C7.67987 12.2353 7.71415 11.6961 7.43699 11.312L5.46394 9.33894C4.60663 8.48163 3.44387 8 2.23146 8Z" fill="currentColor" />
    </svg>
);

const SvgMarketCartAction = (props: React.SVGProps<SVGSVGElement>) => (
    <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg" {...props}>
        <path fillRule="evenodd" clipRule="evenodd" d="M4.00551 0.0887451C4.44574 0.0887451 4.81439 0.422231 4.85837 0.860254L5.18352 4.09857H13.8114C14.0799 4.08889 14.3466 4.14546 14.5881 4.26339C14.833 4.38299 15.0441 4.56187 15.2023 4.78378C15.3533 4.99297 15.4528 5.23493 15.4925 5.48993C15.5322 5.74582 15.5107 6.00751 15.4296 6.25347L15.4291 6.25481L14.0139 10.5109L14.0134 10.5127C13.8995 10.8512 13.6782 11.1432 13.3829 11.344C13.092 11.5417 12.7454 11.6404 12.3944 11.6256H5.70717C5.31402 11.6292 4.93269 11.491 4.63305 11.2363C4.33251 10.9809 4.13442 10.6254 4.07528 10.2354C4.07385 10.226 4.07265 10.2165 4.07169 10.207L3.52055 4.72717C3.51853 4.70706 3.51758 4.68695 3.51768 4.66693L3.23013 1.80303H1.34543C0.872038 1.80303 0.488281 1.41927 0.488281 0.945888C0.488281 0.472501 0.872038 0.0887451 1.34543 0.0887451H4.00551ZM12.1765 13.3078C12.9199 13.3078 13.5226 13.9104 13.5226 14.6538C13.5226 15.3973 12.9199 16 12.1765 16C11.433 16 10.8303 15.3973 10.8303 14.6538C10.8303 13.9104 11.433 13.3078 12.1765 13.3078ZM7.64145 14.6538C7.64145 13.9104 7.03878 13.3078 6.29533 13.3078C5.55189 13.3078 4.94921 13.9104 4.94921 14.6538C4.94921 15.3973 5.55189 16 6.29533 16C7.03878 16 7.64145 15.3973 7.64145 14.6538Z" fill="currentColor" />
    </svg>
);

const SvgCancelSaleAction = (props: React.SVGProps<SVGSVGElement>) => (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" {...props}>
        <path d="M12.0001 14.3008L6.19392 20.1066C5.88439 20.4164 5.50096 20.5712 5.04361 20.5712C4.58626 20.5712 4.20283 20.4164 3.89331 20.1066C3.58358 19.7971 3.42871 19.4137 3.42871 18.9563C3.42871 18.499 3.58358 18.1156 3.89331 17.806L9.69918 11.9999L3.89331 6.19367C3.58358 5.88415 3.42871 5.50071 3.42871 5.04337C3.42871 4.58602 3.58358 4.20258 3.89331 3.89306C4.20283 3.58333 4.58626 3.42847 5.04361 3.42847C5.50096 3.42847 5.88439 3.58333 6.19392 3.89306L12.0001 9.69926L17.8063 3.89306C18.1158 3.58333 18.4993 3.42847 18.9566 3.42847C19.414 3.42847 19.7974 3.58333 20.1069 3.89306C20.4167 4.20258 20.5715 4.58602 20.5715 5.04337C20.5715 5.50071 20.4167 5.88415 20.1069 6.19367L14.301 11.9999L20.1069 17.806C20.4167 18.1156 20.5715 18.499 20.5715 18.9563C20.5715 19.4137 20.4167 19.7971 20.1069 20.1066C19.7974 20.4164 19.414 20.5712 18.9566 20.5712C18.4993 20.5712 18.1158 20.4164 17.8063 20.1066L12.0001 14.3008Z" fill="currentColor" />
    </svg>
);

const SvgEditPriceAction = (props: React.SVGProps<SVGSVGElement>) => (
    <svg width="16" height="16" viewBox="0 0 14 14" fill="none" xmlns="http://www.w3.org/2000/svg" {...props}>
        <path d="M10.715 -0.000976562C10.5152 -0.000976562 10.3174 0.0389464 10.1332 0.116448C9.95019 0.193468 9.78428 0.306058 9.64512 0.447676L1.40732 8.64549C1.34616 8.70635 1.30179 8.782 1.27853 8.86508L0.0185299 13.3651C-0.0301752 13.539 0.0187291 13.7257 0.146458 13.8535C0.274188 13.9812 0.46088 14.0301 0.634827 13.9814L5.13483 12.7214C5.21791 12.6981 5.29356 12.6537 5.35442 12.5926L13.5521 4.3549L13.5535 4.35355C13.6934 4.21438 13.784 4.04847 13.861 3.86548C13.9385 3.68128 13.9784 3.48352 13.9784 3.28369C13.9784 3.08386 13.9385 2.8861 13.861 2.7019C13.784 2.51891 13.6934 2.353 13.5521 2.21383L11.788 0.447676C11.6488 0.306058 11.4829 0.193468 11.2999 0.116448C11.1157 0.0389464 10.9179 -0.000976562 10.715 -0.000976562Z" fill="currentColor" />
    </svg>
);

interface MyUserProps {
    onNavigate: (page: string, category?: any) => void;
    isActive?: boolean;
}

type MainTab = 'users' | 'offers' | 'activity';
type UsersSubTab = 'unlisted' | 'listed';
type OffersSubTab = 'received' | 'placed' | 'bids' | 'users';

const fmt = (n: any) => Number(n || 0).toFixed(2);

interface NamedFilter {
    id: string;
    label: string;
    icon?: React.ReactNode;
}

// Filters are defined inside the component to enable reactive translation

export const MyUser: React.FC<MyUserProps> = ({ onNavigate, isActive = true }) => {
    const { impactOccurred, notificationOccurred } = useHapticFeedback();
    const { user } = useStore();
    const { t } = useTranslation(['common', 'auction', 'marketplace', 'nav', 'nft', 'offers', 'ownership', 'profile', 'staking', 'store', 'username', 'wallet']);
    const { t: tc } = useTranslation('common');

    const UNLISTED_FILTERS: NamedFilter[] = [
        { id: 'all', label: tc('all'), icon: <LayoutGrid className="w-3 h-3 text-white/50" /> },
        { id: 'username', label: t('auction:usernames'), icon: <AtSign className="w-3 h-3 text-cyan-300" /> },
    ];

    const LISTED_FILTERS: NamedFilter[] = [
        { id: 'all', label: tc('all'), icon: <LayoutGrid className="w-3 h-3 text-white/50" /> },
        { id: 'store', label: t('store'), icon: <ShoppingBag className="w-3 h-3 text-emerald-300" /> },
        { id: 'auction', label: t('store:auctions'), icon: <Gavel className="w-3 h-3 text-red-400" /> },
        { id: 'offer', label: t('offers'), icon: <Handshake className="w-3 h-3 text-amber-300" /> },
    ];

    const OFFER_FILTERS: NamedFilter[] = [
        { id: 'all', label: tc('all'), icon: <LayoutGrid className="w-3 h-3 text-white/50" /> },
        { id: 'received', label: t('store:received'), icon: <Archive className="w-3 h-3 text-emerald-300" /> },
        { id: 'placed', label: t('store:sent'), icon: <Send className="w-3 h-3 text-blue-300" /> },
        { id: 'users', label: tc('users'), icon: <UserRound className="w-3 h-3 text-purple-300" /> },
    ];

    const ACTIVITY_FILTERS: NamedFilter[] = [
        { id: 'all', label: tc('all'), icon: <LayoutGrid className="w-3 h-3 text-white/50" /> },
        { id: 'buy', label: tc('buy'), icon: <ShoppingCart className="w-3 h-3 text-red-400" /> },
        { id: 'sell', label: tc('sell'), icon: <ArrowUp className="w-3 h-3 text-emerald-400" /> },
        { id: 'bid', label: t('marketplace:currentBid'), icon: <Gavel className="w-3 h-3 text-amber-300" /> },
        { id: 'offer', label: t('offers'), icon: <Handshake className="w-3 h-3 text-blue-400" /> },
    ];

    // ─── Main Category Filter Tabs ───
    const [activeMainTab, setActiveMainTab] = useState<MainTab>('users');

    // ─── Sub-Tab Switch Controls ───
    const [usersSubTab, setUsersSubTab] = useState<UsersSubTab>('unlisted');
    const [offersSubTab, setOffersSubTab] = useState<OffersSubTab>('received');

    // ─── Search, Filter & View Mode States ───
    const [searchQuery, setSearchQuery] = useState('');
    const [activeFilter, setActiveFilter] = useState('all');
    const [viewMode, setViewMode] = useState<'grid' | 'list' | 'compact' | 'activity'>('grid');

    // ─── Selection State for Bulk Actions ───
    const [selectedItems, setSelectedItems] = useState<string[]>([]);

    // ─── Data States ───
    const [loading, setLoading] = useState(false);
    const [myAssets, setMyAssets] = useState<any[]>([]);
    const [myListings, setMyListings] = useState<any[]>([]);
    const [offersReceived, setOffersReceived] = useState<any[]>([]);
    const [offersSent, setOffersSent] = useState<any[]>([]);
    const [offerHistory, setOfferHistory] = useState<any[]>([]);
    const [myBids, setMyBids] = useState<any[]>([]);
    const [activityLog, setActivityLog] = useState<any[]>([]);
    const [toastMessage, setToastMessage] = useState<string | null>(null);

    // ─── Modal States ───
    const [activeModal, setActiveModal] = useState<'add' | 'withdraw' | 'send' | 'sell' | 'cancel' | 'change_price' | 'counter' | null>(null);
    const [targetItem, setTargetItem] = useState<any | null>(null);
    const [pushMarketItem, setPushMarketItem] = useState<any | null>(null);

    // Modal Form Inputs
    const [formUsername, setFormUsername] = useState('');
    const [formPrice, setFormPrice] = useState('');
    const [formSaleType, setFormSaleType] = useState<'store' | 'auction' | 'offer'>('store');
    const [formDuration, setFormDuration] = useState('24');
    const [formMinInc, setFormMinInc] = useState('1');
    const [formRecipient, setFormRecipient] = useState('');
    const [formAddress, setFormAddress] = useState('');
    const [formCounterAmt, setFormCounterAmt] = useState('');
    const [busyAction, setBusyAction] = useState(false);

    const showToast = (msg: string) => {
        setToastMessage(msg);
        setTimeout(() => setToastMessage(null), 3000);
    };

    // ─── Load Data ───
    const loadData = useCallback(async () => {
        if (!user?.telegram_id) return;
        setLoading(true);
        try {
            // 1. Fetch from username market API
            const res = await usernameMarketApi.my();
            let legacyListings: any[] = [];
            if (res.ok) {
                const d = await res.json();
                if (d.success) {
                    // User's own usernames + active offer-pool usernames (exclude NFTs and unlisted app sessions)
                    const cleanUserAssets = (d.assets || []).filter(
                        (a: any) => (a.ownership_source !== 'app' || a.pool_status === 'offers') && a.asset_type !== 'nft'
                    );
                    setMyAssets(cleanUserAssets);
                    setOffersReceived(d.offers_received || []);
                    setOffersSent(d.offers_sent || []);
                    setOfferHistory(d.offer_history || []);
                    setMyBids(d.bids || []);

                    // Extract offer-pool assets into listings so they show under Offers -> Users
                    for (const a of (d.assets || [])) {
                        if (a.pool_status === 'offers') {
                            legacyListings.push({
                                id: a.asset_id,
                                listing_id: a.asset_id,
                                username: a.username,
                                title: `@${a.username}`,
                                price: Number(a.estimated_value || 0),
                                sale_type: 'offer',
                                status: 'active',
                                created_at: a.created_at,
                                asset_type: 'username'
                            });
                        }
                    }

                    // Extract user's active store and auction listings from legacy
                    if (Array.isArray(d.store_listings)) {
                        legacyListings.push(...d.store_listings.map((l: any) => ({
                            id: l.listing_id || l.id,
                            listing_id: l.listing_id || l.id,
                            username: l.username,
                            title: `@${l.username}`,
                            price: Number(l.ask_price || 0),
                            sale_type: 'store',
                            status: 'active',
                            created_at: l.listed_at || l.created_at,
                            asset_type: 'username'
                        })));
                    }
                    if (Array.isArray(d.auction_listings)) {
                        legacyListings.push(...d.auction_listings.map((l: any) => ({
                            id: l.listing_id || l.id,
                            listing_id: l.listing_id || l.id,
                            username: l.username,
                            title: `@${l.username}`,
                            price: Number(l.current_bid || l.start_price || 0),
                            sale_type: 'auction',
                            status: 'active',
                            created_at: l.starts_at || l.created_at,
                            asset_type: 'username'
                        })));
                    }

                    // Extract activity items
                    const logs: any[] = [];
                    if (d.orders) logs.push(...d.orders.map((o: any) => ({ ...o, type: 'ORDER', date: o.created_at })));
                    if (d.bids) logs.push(...d.bids.map((b: any) => ({ ...b, type: 'BID', date: b.created_at })));
                    if (d.offers_received) logs.push(...d.offers_received.map((o: any) => ({ ...o, type: 'OFFER_RECEIVED', date: o.created_at })));
                    if (d.offers_sent) logs.push(...d.offers_sent.map((o: any) => ({ ...o, type: 'OFFER_SENT', date: o.created_at })));
                    if (d.offer_history) logs.push(...d.offer_history.map((o: any) => ({ ...o, type: 'OFFER_CLOSED', date: o.expired_at || o.rejected_at || o.created_at })));
                    logs.sort((a, b) => new Date(b.date || 0).getTime() - new Date(a.date || 0).getTime());
                    setActivityLog(logs);
                }
            }

            // 2. Fetch v2 user listings - ONLY active / listed listings (exclude cancelled and sold)
            const uid = String(user.telegram_id);
            const v2 = await v2Api.userListings(uid);
            let activeV2Listings: any[] = [];
            if (v2.success && Array.isArray(v2.listings)) {
                activeV2Listings = v2.listings
                    .filter((l: any) => l.status === 'active' || l.status === 'listed')
                    .map((l: any) => ({
                        id: l.id,
                        listing_id: l.id,
                        username: l.username || `item_${l.id}`,
                        title: l.username ? `@${l.username}` : `Item #${l.id}`,
                        price: Number(l.sale_price ?? l.price ?? 0),
                        sale_type: (l.listing_type || 'store').toLowerCase(),
                        status: 'active',
                        created_at: l.created_at,
                        asset_type: l.asset_type || 'username',
                    }));
            }

            // Combine and deduplicate active listings
            const seenHandles = new Set<string>();
            const combinedListings: any[] = [];
            for (const l of [...activeV2Listings, ...legacyListings]) {
                const u = String(l.username || '').toLowerCase().replace(/^@/, '');
                if (u && !seenHandles.has(u)) {
                    seenHandles.add(u);
                    combinedListings.push(l);
                }
            }
            setMyListings(combinedListings);
        } catch (e) {
            console.error('[MyUser] Load error:', e);
        } finally {
            setLoading(false);
        }
    }, [user]);

    useEffect(() => {
        if (isActive) loadData();
    }, [isActive, loadData]);

    // ─── Live sync: reload when the market flow changes (list/sell/offer/bid) ───
    useEffect(() => {
        if (!isActive) return;
        socket.on('market_update', loadData);
        socket.on('push_usernames_updated', loadData);
        socket.on('username_listed', loadData);
        socket.on('username_market_update', loadData);
        return () => {
            socket.off('market_update', loadData);
            socket.off('push_usernames_updated', loadData);
            socket.off('username_listed', loadData);
            socket.off('username_market_update', loadData);
        };
    }, [isActive, loadData]);

    // ─── Categorize Assets (Unlisted vs Listed) ───
    const unlistedItems = useMemo(() => {
        let items = myAssets.filter((a: any) => String(a.pool_status || '').toLowerCase() === 'unlisted');
        // Exclude app push sessions and NFTs
        items = items.filter((a: any) => a.ownership_source !== 'app' && a.asset_type !== 'nft');
        if (searchQuery.trim()) {
            const q = searchQuery.toLowerCase().trim();
            items = items.filter((i: any) => (i.username && i.username.toLowerCase().includes(q)) || String(i.asset_id).includes(q));
        }
        if (activeFilter === 'username') items = items.filter((i: any) => i.asset_type !== 'nft');
        return items;
    }, [myAssets, searchQuery, activeFilter]);

    const listedItems = useMemo(() => {
        let items = [...myListings];

        if (searchQuery.trim()) {
            const q = searchQuery.toLowerCase().trim();
            items = items.filter((i: any) => (i.username && i.username.toLowerCase().includes(q)) || String(i.id).includes(q));
        }

        if (activeFilter !== 'all') {
            items = items.filter((i: any) => String(i.sale_type || 'store').toLowerCase() === activeFilter);
        }

        return items;
    }, [myListings, searchQuery, activeFilter]);

    // ─── Offers Filter ───
    const filteredOffers = useMemo(() => {
        let list: any[] = [];
        if (offersSubTab === 'received') {
            list = [...offersReceived];
        } else if (offersSubTab === 'placed') {
            list = [...offersSent];
        } else if (offersSubTab === 'bids') {
            list = [...myBids];
        } else {
            list = [...myListings.filter((l: any) => l.sale_type === 'offer'), ...offersReceived];
        }

        if (searchQuery.trim()) {
            const q = searchQuery.toLowerCase().trim();
            list = list.filter((o: any) => o.username && o.username.toLowerCase().includes(q));
        }

        if (activeFilter === 'received') list = offersReceived;
        else if (activeFilter === 'placed') list = offersSent;
        else if (activeFilter === 'bids') list = myBids;
        else if (activeFilter === 'users') list = myListings.filter((l: any) => l.sale_type === 'offer');

        return list;
    }, [offersSubTab, offersReceived, offersSent, myBids, myListings, searchQuery, activeFilter]);

    // ─── Activity Grouping & Sample Data (Matching target screenshot) ───
    const sampleActivityData = [
        { id: 'a1', title: 'Liberty Figure', actionType: 'Buy', amountDisplay: '-4.85 GRAM', isPositive: false, dateStr: '15 SEPTEMBER 2026', emoji: '🦩', type: 'buy' },
        { id: 'a2', title: 'Victory Medal', actionType: 'Buy', amountDisplay: '-4.68 GRAM', isPositive: false, dateStr: '9 SEPTEMBER 2026', emoji: '🏅', type: 'buy' },
        { id: 'a3', title: 'Victory Medal', actionType: 'Buy', amountDisplay: '-5.09 GRAM', isPositive: false, dateStr: '9 SEPTEMBER 2026', emoji: '🥈', type: 'buy' },
        { id: 'a4', title: 'Neko Helmet', actionType: 'Buy', amountDisplay: '-40 GRAM', isPositive: false, dateStr: '4 SEPTEMBER 2026', emoji: '🪖', type: 'buy' },
        { id: 'a5', title: 'Chill Flame', actionType: 'Buy', amountDisplay: '-2.48 GRAM', isPositive: false, dateStr: '25 MARCH 2026', emoji: '🗽', type: 'buy' },
    ];

    const displayActivities = useMemo(() => {
        let list = activityLog.length > 0 ? activityLog.map((a: any, i: number) => {
            const dateObj = new Date(a.date || Date.now());
            const dateStr = `${dateObj.getDate()} ${dateObj.toLocaleString('en-US', { month: 'long' }).toUpperCase()} ${dateObj.getFullYear()}`;
            const typeLower = String(a.type || 'BUY').toLowerCase();
            const isPos = typeLower.includes('sell') || typeLower.includes('refund');
            const amtVal = fmt(a.amount || a.price || 0);
            return {
                id: a.id || `act-${i}`,
                title: a.username ? `@${a.username}` : (a.title || `Item #${a.id || i}`),
                actionType: a.actionType || (typeLower.includes('buy') ? 'Buy' : typeLower.includes('sell') ? 'Sell' : a.type || 'Buy'),
                amountDisplay: `${isPos ? '+' : '-'}${amtVal} GRAM`,
                isPositive: isPos,
                dateStr,
                emoji: '💎',
                type: typeLower.includes('buy') ? 'buy' : typeLower.includes('sell') ? 'sell' : 'offer'
            };
        }) : sampleActivityData;

        if (searchQuery.trim()) {
            const q = searchQuery.toLowerCase().trim();
            list = list.filter((item: any) => item.title.toLowerCase().includes(q) || item.actionType.toLowerCase().includes(q) || item.dateStr.toLowerCase().includes(q));
        }

        if (activeFilter !== 'all') {
            list = list.filter((item: any) => item.type.includes(activeFilter) || item.actionType.toLowerCase().includes(activeFilter));
        }

        const groups: { [date: string]: typeof list } = {};
        list.forEach((item: any) => {
            if (!groups[item.dateStr]) groups[item.dateStr] = [];
            groups[item.dateStr].push(item);
        });

        return Object.entries(groups);
    }, [activityLog, searchQuery, activeFilter]);

    // ─── Actions handlers ───
    const handleAddAsset = async () => {
        if (!formUsername.trim()) return;
        setBusyAction(true);
        try {
            const clean = formUsername.replace(/^@/, '').trim();
            await v2Api.registerOwnership(clean);
            showToast(`@${clean} added to your account`);
            setActiveModal(null);
            setFormUsername('');
            await loadData();
        } catch (e: any) {
            showToast(e.message || 'Error adding username');
        } finally {
            setBusyAction(false);
        }
    };

    const handleDeleteAsset = async () => {
        const item = targetItem || (selectedItems.length > 0 ? unlistedItems.find(i => String(i.asset_id || i.id) === selectedItems[0]) : null);
        if (!item) {
            showToast('Select a username to delete');
            return;
        }
        setBusyAction(true);
        try {
            await v2Api.deleteOwnership({ id: item.asset_id || item.id, nft_name: item.username });
            showToast(`@${item.username} removed from your account`);
            setActiveModal(null);
            setSelectedItems([]);
            await loadData();
        } catch (e: any) {
            showToast(e.message || 'Error deleting username');
        } finally {
            setBusyAction(false);
        }
    };

    const handleCreateListing = async () => {
        if (!formPrice || Number(formPrice) <= 0) return;
        setBusyAction(true);
        try {
            const item = targetItem || (selectedItems.length > 0 ? unlistedItems.find(i => String(i.asset_id) === selectedItems[0]) : null);
            if (!item) throw new Error('Select a username to list');

            const clean = String(item.username).replace(/^@/, '').trim();
            await v2Api.createUsernameListing({
                username: clean,
                listingType: formSaleType,
                price: Number(formPrice),
                durationHours: formSaleType === 'auction' ? Number(formDuration) : undefined,
                minIncrement: formSaleType === 'auction' ? Number(formMinInc) : undefined,
            });

            notificationOccurred('success');
            showToast(`@${clean} listed successfully as ${formSaleType.toUpperCase()}`);
            setActiveModal(null);
            setSelectedItems([]);
            setFormPrice('');
            await loadData();
        } catch (e: any) {
            notificationOccurred('error');
            showToast(e.message || 'Failed to list');
        } finally {
            setBusyAction(false);
        }
    };

    const handleCancelListing = async (item?: any) => {
        const toCancel = item || (selectedItems.length > 0 ? listedItems.find(i => String(i.id) === selectedItems[0]) : null);
        if (!toCancel) return;
        setBusyAction(true);
        try {
            await v2Api.deactivate(toCancel.id || toCancel.listing_id);
            notificationOccurred('success');
            showToast(`Listing for @${toCancel.username} cancelled`);
            setActiveModal(null);
            setSelectedItems([]);
            await loadData();
        } catch (e: any) {
            showToast(e.message || 'Failed to cancel listing');
        } finally {
            setBusyAction(false);
        }
    };

    const handleChangePrice = async () => {
        const item = targetItem || (selectedItems.length > 0 ? listedItems.find(i => String(i.id) === selectedItems[0]) : null);
        if (!item || !formPrice || Number(formPrice) <= 0) return;
        setBusyAction(true);
        try {
            const clean = String(item.username).replace(/^@/, '').trim();
            await v2Api.createUsernameListing({
                username: clean,
                listingType: item.sale_type || 'store',
                price: Number(formPrice),
            });
            notificationOccurred('success');
            showToast(`Price for @${clean} updated to ${formPrice} GRAM`);
            setActiveModal(null);
            setSelectedItems([]);
            setFormPrice('');
            await loadData();
        } catch (e: any) {
            showToast(e.message || 'Failed to update price');
        } finally {
            setBusyAction(false);
        }
    };

    const handleSendUsername = async () => {
        if (!formRecipient.trim()) return;
        const item = targetItem || (selectedItems.length > 0 ? unlistedItems.find(i => String(i.asset_id) === selectedItems[0]) : null);
        if (!item) return;
        setBusyAction(true);
        try {
            showToast(`Transfer of @${item.username} to ${formRecipient} initiated`);
            setActiveModal(null);
            setFormRecipient('');
            setSelectedItems([]);
            await loadData();
        } catch (e: any) {
            showToast(e.message || 'Transfer failed');
        } finally {
            setBusyAction(false);
        }
    };

    const handleWithdraw = async () => {
        const item = targetItem || (selectedItems.length > 0 ? unlistedItems.find(i => String(i.asset_id) === selectedItems[0]) : null);
        if (!item) return;
        setBusyAction(true);
        try {
            showToast(`Withdrawal request for @${item.username} submitted`);
            setActiveModal(null);
            setSelectedItems([]);
            await loadData();
        } catch (e: any) {
            showToast(e.message || 'Withdrawal failed');
        } finally {
            setBusyAction(false);
        }
    };

    const handleOfferAction = async (offer: any, action: 'accept' | 'reject' | 'cancel') => {
        setBusyAction(true);
        try {
            if (action === 'accept') {
                await usernameMarketApi.offerAction(offer.offer_id, { action: 'accept' });
                showToast(`Accepted offer on @${offer.username}`);
            } else if (action === 'reject') {
                await usernameMarketApi.offerAction(offer.offer_id, { action: 'reject' });
                showToast(`Rejected offer on @${offer.username}`);
            } else {
                await usernameMarketApi.offerAction(offer.offer_id, { action: 'cancel' });
                showToast(`Cancelled offer on @${offer.username}`);
            }
            notificationOccurred('success');
            await loadData();
        } catch (e: any) {
            showToast(e.message || 'Action failed');
        } finally {
            setBusyAction(false);
        }
    };

    const handleCounterOffer = async () => {
        if (!targetItem || !formCounterAmt || Number(formCounterAmt) <= 0) return;
        setBusyAction(true);
        try {
            await usernameMarketApi.offerAction(targetItem.offer_id, { action: 'counter', counterAmount: Number(formCounterAmt) });
            notificationOccurred('success');
            showToast(`Counter offer of ${formCounterAmt} GRAM sent for @${targetItem.username}`);
            setActiveModal(null);
            setFormCounterAmt('');
            await loadData();
        } catch (e: any) {
            showToast(e.message || 'Failed to send counter offer');
        } finally {
            setBusyAction(false);
        }
    };

    return (
        <div className="pt-4 min-h-screen pb-24 text-white">
            {/* Balance Bar Header */}
            <div className="px-4 mb-2">
                <BalanceBar
                    onDepositClick={() => onNavigate('wallet', 'deposit')}
                    onHistoryClick={() => onNavigate('p2p_history')}
                    logoVariant="lottie"
                    logoActive={isActive}
                    logoIcon={<Users size={20} className="text-cyan-300" />}
                />
            </div>

            {/* ─── 1. TOP CATEGORY TITLE TABS (matching target screenshot exact design) ─── */}
            <div className="flex items-center gap-4 px-4 pt-1 pb-3 overflow-x-auto no-scrollbar">
                <button
                    onClick={() => { impactOccurred('light'); setActiveMainTab('users'); setActiveFilter('all'); }}
                    className={`text-2xl font-extrabold tracking-tight transition-colors duration-200 shrink-0 ${activeMainTab === 'users' ? 'text-white font-extrabold' : 'text-[#8e8e93]/60 font-bold hover:text-white'
                        }`}
                >
                    {tc('users')}
                </button>
                <button
                    onClick={() => { impactOccurred('light'); setActiveMainTab('offers'); setActiveFilter('all'); }}
                    className={`text-2xl font-extrabold tracking-tight transition-colors duration-200 shrink-0 ${activeMainTab === 'offers' ? 'text-white font-extrabold' : 'text-[#8e8e93]/60 font-bold hover:text-white'
                        }`}
                >
                    {t('marketplace:tabOffers')}
                </button>
                <button
                    onClick={() => { impactOccurred('light'); setActiveMainTab('activity'); setActiveFilter('all'); }}
                    className={`text-2xl font-extrabold tracking-tight transition-colors duration-200 shrink-0 ${activeMainTab === 'activity' ? 'text-white font-extrabold' : 'text-[#8e8e93]/60 font-bold hover:text-white'
                        }`}
                >
                    {t('myActivity', 'activity')}
                </button>
            </div>

            {/* ═════════════════════════════════════════════════════════
                MAIN TAB 1: USERS
            ═════════════════════════════════════════════════════════ */}
            {activeMainTab === 'users' && (
                <div className="space-y-3">
                    {/* ─── 2. SWITCH CAPSULE BAR (Pure white active pill matching target screenshot) ─── */}
                    {/* ─── 2. SWITCH CAPSULE BAR (Enlarged by 5%) ─── */}
                    <div className="px-4">
                        <div className="p-[2.5px] rounded-full bg-[#2c2c2e] flex items-center">
                            <button
                                onClick={() => { impactOccurred('light'); setUsersSubTab('unlisted'); setSelectedItems([]); setActiveFilter('all'); }}
                                className={`flex-1 py-1.5 px-3.5 rounded-full text-xs font-extrabold transition-all duration-200 flex items-center justify-center gap-1.5 ${usersSubTab === 'unlisted'
                                        ? 'bg-white text-black shadow-md'
                                        : 'text-[#8e8e93] hover:text-white'
                                    }`}
                            >
                                <span>{t('unlisted', 'Unlisted')}</span>
                                <span className={`text-[10px] px-1.5 py-0.5 rounded-full font-bold transition-all ${usersSubTab === 'unlisted' ? 'bg-[#8e8e93]/30 text-black' : 'bg-[#3a3a3c] text-[#8e8e93]'
                                    }`}>
                                    {unlistedItems.length}
                                </span>
                            </button>
                            <button
                                onClick={() => { impactOccurred('light'); setUsersSubTab('listed'); setSelectedItems([]); setActiveFilter('all'); }}
                                className={`flex-1 py-1.5 px-3.5 rounded-full text-xs font-extrabold transition-all duration-200 flex items-center justify-center gap-1.5 ${usersSubTab === 'listed'
                                        ? 'bg-white text-black shadow-md'
                                        : 'text-[#8e8e93] hover:text-white'
                                    }`}
                            >
                                <span>{t('listed', 'Listed')}</span>
                                <span className={`text-[10px] px-1.5 py-0.5 rounded-full font-bold transition-all ${usersSubTab === 'listed' ? 'bg-[#8e8e93]/30 text-black' : 'bg-[#3a3a3c] text-[#8e8e93]'
                                    }`}>
                                    {listedItems.length}
                                </span>
                            </button>
                        </div>
                    </div>

                    {/* ─── 3. ACTION BAR (Exact design & SVGs from dev.portals-market.com) ─── */}
                    <div className="px-4">
                        <div className="bg-[#2c2c2e] rounded-[18px] h-[60px] px-3 flex items-center justify-around">
                            {usersSubTab === 'unlisted' ? (
                                <>
                                    <button
                                        onClick={() => { impactOccurred('light'); setActiveModal('add'); }}
                                        className="flex flex-col items-center justify-center gap-1.5 hover:opacity-80 active:scale-95 transition-all flex-1 cursor-pointer"
                                    >
                                        <SvgPlusAction className="w-4 h-4 text-white" />
                                        <span className="text-[11px] font-medium text-white tracking-tight">{tc('add')}</span>
                                    </button>
                                    <button
                                        onClick={() => { impactOccurred('light'); setActiveModal('withdraw'); }}
                                        className="flex flex-col items-center justify-center gap-1.5 hover:opacity-80 active:scale-95 transition-all flex-1 cursor-pointer"
                                    >
                                        <SvgAnchorArrowAction className="w-4 h-4 text-white" />
                                        <span className="text-[11px] font-medium text-white tracking-tight">{tc('withdraw')}</span>
                                    </button>
                                    <button
                                        onClick={() => { impactOccurred('light'); setActiveModal('send'); }}
                                        className="flex flex-col items-center justify-center gap-1.5 hover:opacity-80 active:scale-95 transition-all flex-1 cursor-pointer"
                                    >
                                        <SvgSendGiftAction className="w-4 h-4 text-white" />
                                        <span className="text-[11px] font-medium text-white tracking-tight">{tc('transfer')}</span>
                                    </button>
                                    <button
                                        onClick={() => { impactOccurred('light'); handleDeleteAsset(); }}
                                        className="flex flex-col items-center justify-center gap-1.5 hover:opacity-80 active:scale-95 transition-all flex-1 cursor-pointer"
                                    >
                                        <Trash2 className="w-4 h-4 text-white" />
                                        <span className="text-[11px] font-medium text-white tracking-tight">{tc('delete', 'Delete')}</span>
                                    </button>
                                    <button
                                        onClick={() => {
                                            impactOccurred('light');
                                            let item = targetItem || (selectedItems.length > 0 ? unlistedItems.find(i => String(i.asset_id || i.id) === selectedItems[0]) : null);
                                            if (!item) {
                                                showToast('Select a username to sell');
                                                return;
                                            }
                                            // Ensure PushMarketModal treats it as a username instead of NFT
                                            item = { ...item, is_ownership: item.asset_type !== 'nft' };
                                            setPushMarketItem(item);
                                        }}
                                        className="flex flex-col items-center justify-center gap-1.5 hover:opacity-80 active:scale-95 transition-all flex-1 cursor-pointer"
                                    >
                                        <SvgMarketCartAction className="w-4 h-4 text-white" />
                                        <span className="text-[11px] font-medium text-white tracking-tight">{tc('sell')}</span>
                                    </button>
                                </>
                            ) : (
                                <>
                                    <button
                                        onClick={() => { impactOccurred('light'); setActiveModal('cancel'); }}
                                        className="flex flex-col items-center justify-center gap-1.5 hover:opacity-80 active:scale-95 transition-all flex-1 cursor-pointer"
                                    >
                                        <SvgCancelSaleAction className="w-4 h-4 text-white" />
                                        <span className="text-[11px] font-medium text-white tracking-tight">{t('store:cancelListing')}</span>
                                    </button>
                                    <button
                                        onClick={() => { impactOccurred('light'); setActiveModal('change_price'); }}
                                        className="flex flex-col items-center justify-center gap-1.5 hover:opacity-80 active:scale-95 transition-all flex-1 cursor-pointer"
                                    >
                                        <SvgEditPriceAction className="w-4 h-4 text-white" />
                                        <span className="text-[11px] font-medium text-white tracking-tight">{t('changePrice', 'Change price')}</span>
                                    </button>
                                </>
                            )}
                        </div>
                    </div>

                    {/* ─── 4. SEARCH AND FILTER BAR (Store page component) ─── */}
                    <div>
                        <ExperimentalFilterBar
                            quickFind={searchQuery}
                            onQuickFindChange={(val) => setSearchQuery(val)}
                            viewMode={viewMode}
                            onViewModeChange={setViewMode}
                            items={usersSubTab === 'unlisted' ? unlistedItems : listedItems}
                            filters={usersSubTab === 'unlisted' ? UNLISTED_FILTERS : LISTED_FILTERS}
                            activeFilter={activeFilter}
                            onFilterSelect={(id) => setActiveFilter(id)}
                            onPurchasesClick={() => onNavigate('purchases')}
                        />
                    </div>

                    {/* ─── 5. CONTENT GRID / EMPTY STATE (Matching target screenshot design) ─── */}
                    <div className="px-4">
                        {loading ? (
                            <div className="flex justify-center py-12">
                                <RefreshCw className="w-6 h-6 text-cyan-400 animate-spin" />
                            </div>
                        ) : usersSubTab === 'unlisted' && unlistedItems.length === 0 ? (
                            /* Empty state styled with Custom Emoji 105 */
                            <div className="flex flex-col items-center justify-center py-12 text-center">
                                <div className="w-24 h-24 mb-4 flex items-center justify-center">
                                    <CustomEmojiPlayer emojiId="105" size={96} />
                                </div>
                                <h3 className="text-lg font-bold text-white mb-1">{t('store:noUsernamesTitle')}</h3>
                                <p className="text-xs text-[#8e8e93] max-w-[260px] mb-6">
                                    {t('store:noUsernamesDesc')}
                                </p>
                                <button
                                    onClick={() => onNavigate('store')}
                                    className="px-8 py-3 rounded-full bg-white text-black font-extrabold text-sm hover:bg-white/90 active:scale-95 transition-all shadow-lg"
                                >
                                    {t('goToMarket', 'Go to market')}
                                </button>
                            </div>
                        ) : usersSubTab === 'listed' && listedItems.length === 0 ? (
                            /* Empty state styled with Custom Emoji 106 */
                            <div className="flex flex-col items-center justify-center py-12 text-center">
                                <div className="w-24 h-24 mb-4 flex items-center justify-center">
                                    <CustomEmojiPlayer emojiId="106" size={96} />
                                </div>
                                <h3 className="text-sm font-medium text-white mb-1">{t('store:noStoreListingsTitle')}</h3>
                            </div>
                        ) : (
                            <div className="grid grid-cols-1 gap-2.5">
                                {(usersSubTab === 'unlisted' ? unlistedItems : listedItems).map((item: any) => {
                                    const itemKey = String(item.asset_id || item.id);
                                    const isSelected = selectedItems.includes(itemKey);
                                    return (
                                        <div
                                            key={`user-card-${itemKey}`}
                                            onClick={() => {
                                                impactOccurred('light');
                                                setSelectedItems(isSelected ? selectedItems.filter(id => id !== itemKey) : [itemKey]);
                                            }}
                                            className={`bg-[#2c2c2e] rounded-2xl p-4 transition-all cursor-pointer relative border ${isSelected ? 'border-cyan-400 bg-cyan-500/10' : 'border-transparent hover:border-white/10'
                                                }`}
                                        >
                                            <div className="flex items-center justify-between">
                                                <div className="flex items-center gap-3.5 min-w-0">
                                                    {(item.session_avatar || item.session_info?.avatar) ? (
                                                        <img
                                                            src={item.session_avatar || item.session_info?.avatar}
                                                            alt={item.username}
                                                            className="w-11 h-11 rounded-2xl object-cover border border-cyan-500/30 shrink-0"
                                                            onError={(e) => { (e.target as HTMLImageElement).src = '/icons/default-avatar.svg'; }}
                                                        />
                                                    ) : (
                                                        <div className="w-11 h-11 rounded-2xl bg-white/5 border border-white/10 flex items-center justify-center text-cyan-300 shrink-0">
                                                            <AtSign size={20} />
                                                        </div>
                                                    )}
                                                    <div className="min-w-0">
                                                        <div className="flex items-center gap-1.5">
                                                            <h4 className="text-base font-extrabold text-white truncate">@{item.username.replace(/^@/, '')}</h4>
                                                            {(item.ownership_source === 'app' || item.session_phone || item.session_info) && (
                                                                <span className="text-[9px] px-1.5 py-0.5 rounded-md bg-cyan-500/20 text-cyan-300 border border-cyan-500/30 font-bold uppercase shrink-0">
                                                                    {t('username:session')}
                                                                </span>
                                                            )}
                                                        </div>
                                                        {(item.session_phone || item.session_info?.phone) ? (
                                                            <p className="text-xs font-mono text-cyan-300/90 truncate mt-0.5 dir-ltr">
                                                                📱 {item.session_name || item.session_info?.name ? `${item.session_name || item.session_info?.name} ` : ''}({item.session_phone || item.session_info?.phone})
                                                            </p>
                                                        ) : (
                                                            <p className="text-xs font-semibold text-[#8e8e93] uppercase">
                                                                {usersSubTab === 'unlisted' ? `Asset #${item.asset_id}` : (item.sale_type || 'store').toUpperCase()}
                                                            </p>
                                                        )}
                                                    </div>
                                                </div>
                                                <div className="text-right flex items-center gap-1.5 shrink-0">
                                                    <span className="text-base font-extrabold text-sky-400">
                                                        {(item.price || item.estimated_value) > 0 ? fmt(item.price || item.estimated_value) : '--'}
                                                    </span>
                                                    {(item.price || item.estimated_value) > 0 && <GramIcon size={14} />}
                                                </div>
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                        )}
                    </div>
                </div>
            )}

            {/* ═════════════════════════════════════════════════════════
                MAIN TAB 2: OFFERS
            ═════════════════════════════════════════════════════════ */}
            {activeMainTab === 'offers' && (
                <div className="space-y-3">
                    {/* Switch Toggle: Received [0] Placed [0] Users[0] */}
                    {/* Switch Toggle: Received [0] Placed [0] Users[0] (Enlarged by 5%) */}
                    <div className="px-4">
                        <div className="p-[2.5px] rounded-full bg-[#2c2c2e] flex items-center">
                            <button
                                onClick={() => { impactOccurred('light'); setOffersSubTab('received'); setActiveFilter('all'); }}
                                className={`flex-1 py-1.5 px-2 rounded-full text-[11px] font-extrabold transition-all flex items-center justify-center gap-1 ${offersSubTab === 'received' ? 'bg-white text-black shadow-md' : 'text-[#8e8e93] hover:text-white'
                                    }`}
                            >
                                <span>{t('store:received')}</span>
                                <span className={`text-[10px] px-1.5 py-0.5 rounded-full font-bold ${offersSubTab === 'received' ? 'bg-[#8e8e93]/30 text-black' : 'bg-[#3a3a3c] text-[#8e8e93]'
                                    }`}>
                                    {offersReceived.length}
                                </span>
                            </button>
                            <button
                                onClick={() => { impactOccurred('light'); setOffersSubTab('placed'); setActiveFilter('all'); }}
                                className={`flex-1 py-1.5 px-2 rounded-full text-[11px] font-extrabold transition-all flex items-center justify-center gap-1 ${offersSubTab === 'placed' ? 'bg-white text-black shadow-md' : 'text-[#8e8e93] hover:text-white'
                                    }`}
                            >
                                <span>{t('profile:placed')}</span>
                                <span className={`text-[10px] px-1.5 py-0.5 rounded-full font-bold ${offersSubTab === 'placed' ? 'bg-[#8e8e93]/30 text-black' : 'bg-[#3a3a3c] text-[#8e8e93]'
                                    }`}>
                                    {offersSent.length}
                                </span>
                            </button>
                            <button
                                onClick={() => { impactOccurred('light'); setOffersSubTab('bids'); setActiveFilter('all'); }}
                                className={`flex-1 py-1.5 px-2 rounded-full text-[11px] font-extrabold transition-all flex items-center justify-center gap-1 ${offersSubTab === 'bids' ? 'bg-white text-black shadow-md' : 'text-[#8e8e93] hover:text-white'
                                    }`}
                            >
                                <span>{t('marketplace:tabBids')}</span>
                                <span className={`text-[10px] px-1.5 py-0.5 rounded-full font-bold ${offersSubTab === 'bids' ? 'bg-[#8e8e93]/30 text-black' : 'bg-[#3a3a3c] text-[#8e8e93]'
                                    }`}>
                                    {myBids.length}
                                </span>
                            </button>
                            <button
                                onClick={() => { impactOccurred('light'); setOffersSubTab('users'); setActiveFilter('all'); }}
                                className={`flex-1 py-1.5 px-2 rounded-full text-[11px] font-extrabold transition-all flex items-center justify-center gap-1 ${offersSubTab === 'users' ? 'bg-white text-black shadow-md' : 'text-[#8e8e93] hover:text-white'
                                    }`}
                            >
                                <span>{t('users')}</span>
                                <span className={`text-[10px] px-1.5 py-0.5 rounded-full font-bold ${offersSubTab === 'users' ? 'bg-[#8e8e93]/30 text-black' : 'bg-[#3a3a3c] text-[#8e8e93]'
                                    }`}>
                                    {myListings.filter(l => l.sale_type === 'offer').length}
                                </span>
                            </button>
                        </div>
                    </div>

                    {/* Search & Filter Component */}
                    <div>
                        <ExperimentalFilterBar
                            quickFind={searchQuery}
                            onQuickFindChange={(val) => setSearchQuery(val)}
                            viewMode={viewMode}
                            onViewModeChange={setViewMode}
                            items={filteredOffers}
                            filters={OFFER_FILTERS}
                            activeFilter={activeFilter}
                            onFilterSelect={(id) => setActiveFilter(id)}
                            onPurchasesClick={() => onNavigate('purchases')}
                        />
                    </div>

                    {/* Offers Cards */}
                    <div className="px-4">
                        {filteredOffers.length === 0 ? (
                            <div className="flex flex-col items-center justify-center py-12 text-center">
                                <div className="w-24 h-24 mb-4 flex items-center justify-center">
                                    <CustomEmojiPlayer emojiId="107" size={96} />
                                </div>
                                <h3 className="text-sm font-medium text-white mb-1">It's empty so far</h3>
                            </div>
                        ) : (
                            <div className="space-y-3">
                                {filteredOffers.map((o: any) => {
                                    if (offersSubTab === 'bids') {
                                        const isHighest = o.bid_status === 'HIGHEST';
                                        const isOutbid = o.bid_status === 'OUTBID';
                                        const isWon = o.bid_status === 'WON';
                                        return (
                                            <div key={`bid-${o.bid_id || o.id}`} className="bg-[#2c2c2e] rounded-2xl p-4 border border-white/[0.06]">
                                                <div className="flex items-center justify-between mb-2">
                                                    <div className="flex items-center gap-3">
                                                        <div className={`w-10 h-10 rounded-xl border flex items-center justify-center ${isHighest ? 'bg-emerald-500/10 border-emerald-500/20 text-emerald-300' :
                                                                isOutbid ? 'bg-orange-500/10 border-orange-500/20 text-orange-300' :
                                                                    isWon ? 'bg-cyan-500/10 border-cyan-500/20 text-cyan-300' :
                                                                        'bg-white/5 border-white/10 text-white/50'
                                                            }`}>
                                                            <Gavel size={18} />
                                                        </div>
                                                        <div>
                                                            <h4 className="text-sm font-bold text-white">@{o.username}</h4>
                                                            <span className={`text-[10px] font-extrabold px-2 py-0.5 rounded-full uppercase ${isHighest ? 'bg-emerald-500/20 text-emerald-300' :
                                                                    isOutbid ? 'bg-orange-500/20 text-orange-300' :
                                                                        isWon ? 'bg-cyan-500/20 text-cyan-300' :
                                                                            'bg-white/10 text-white/50'
                                                                }`}>
                                                                {o.bid_status || 'ACTIVE'}
                                                            </span>
                                                        </div>
                                                    </div>
                                                    <div className="text-right">
                                                        <div className="text-[10px] text-white/40 font-semibold">{t('marketplace:myBid', 'My Bid')}</div>
                                                        <span className="text-base font-black text-white flex items-center gap-1 justify-end">
                                                            {fmt(o.bid_amount)} <GramIcon size={12} />
                                                        </span>
                                                    </div>
                                                </div>

                                                <div className="mt-3 pt-3 border-t border-white/5 flex items-center justify-between">
                                                    <div className="text-xs text-white/50">
                                                        {t('marketplace:currentBid')}: <b className="text-white">{fmt(o.current_bid || o.bid_amount)} GRAM</b>
                                                    </div>
                                                    <button
                                                        onClick={() => {
                                                            impactOccurred('light');
                                                            onNavigate('auction_bid', { listing_id: o.listing_id, username: o.username, id: o.listing_id });
                                                        }}
                                                        className={`px-3 py-1.5 rounded-xl text-xs font-bold flex items-center gap-1.5 transition-all ${isOutbid
                                                                ? 'bg-orange-500/20 text-orange-300 border border-orange-500/30 hover:bg-orange-500/30'
                                                                : 'bg-white/10 text-white hover:bg-white/20'
                                                            }`}
                                                    >
                                                        {isOutbid ? t('marketplace:raiseBid', 'Raise Bid') : t('marketplace:viewAuction', 'View Auction')}
                                                    </button>
                                                </div>
                                            </div>
                                        );
                                    }

                                    return (
                                        <div key={`offer-${o.offer_id || o.id}`} className="bg-[#2c2c2e] rounded-2xl p-4 border border-white/[0.06]">
                                            <div className="flex items-center justify-between mb-2">
                                                <div className="flex items-center gap-3">
                                                    <div className="w-10 h-10 rounded-xl bg-white/5 border border-white/10 flex items-center justify-center text-orange-300">
                                                        <Handshake size={18} />
                                                    </div>
                                                    <div>
                                                        <h4 className="text-sm font-bold text-white">@{o.username}</h4>
                                                        <span className="text-[10px] font-bold text-[#8e8e93] uppercase">
                                                            Offer #{o.offer_id || o.id}
                                                        </span>
                                                    </div>
                                                </div>
                                                <div className="text-right">
                                                    <span className="text-base font-black text-orange-300 flex items-center gap-1 justify-end">
                                                        {fmt(o.amount || o.price)} <GramIcon size={12} />
                                                    </span>
                                                </div>
                                            </div>

                                            {offersSubTab === 'received' && (o.status === 'PENDING' || !o.status) && (
                                                <div className="mt-3 pt-3 border-t border-white/5 flex gap-2">
                                                    <button
                                                        onClick={() => handleOfferAction(o, 'accept')}
                                                        disabled={busyAction}
                                                        className="flex-1 py-2 rounded-xl bg-emerald-500/20 border border-emerald-500/30 text-emerald-300 text-xs font-bold flex items-center justify-center gap-1"
                                                    >
                                                        <Check size={14} /> {t('marketplace:accept')}
                                                    </button>
                                                    <button
                                                        onClick={() => {
                                                            setTargetItem(o);
                                                            setFormCounterAmt(String(o.amount || ''));
                                                            setActiveModal('counter');
                                                        }}
                                                        className="flex-1 py-2 rounded-xl bg-orange-500/20 border border-orange-500/30 text-orange-300 text-xs font-bold flex items-center justify-center gap-1"
                                                    >
                                                        <CornerDownLeft size={14} /> {t('marketplace:counter')}
                                                    </button>
                                                    <button
                                                        onClick={() => handleOfferAction(o, 'reject')}
                                                        disabled={busyAction}
                                                        className="px-3 py-2 rounded-xl bg-red-500/20 border border-red-500/30 text-red-300 text-xs font-bold"
                                                    >
                                                        <X size={14} />
                                                    </button>
                                                </div>
                                            )}
                                        </div>
                                    );
                                })}
                            </div>
                        )}
                        {offerHistory.length > 0 && (
                            <div className="mt-6 px-1">
                                <div className="flex items-center justify-between mb-2 px-1">
                                    <h3 className="text-[11px] font-black text-[#8e8e93] uppercase tracking-widest flex items-center gap-1.5">
                                        <X size={12} className="text-amber-500" /> {t('offers:endedOffers')}
                                    </h3>
                                    <span className="text-[9px] font-bold text-amber-400/80">{offerHistory.length} closed</span>
                                </div>
                                <div className="space-y-2">
                                    {offerHistory.map((o: any, i: number) => (
                                        <div key={`oh-${o.offer_id}-${i}`} className="bg-[#262628] rounded-2xl px-4 py-3 border border-white/[0.05] opacity-85 flex items-center justify-between">
                                            <div className="flex items-center gap-3 min-w-0">
                                                <div className="w-9 h-9 rounded-xl bg-white/[0.04] border border-white/[0.06] flex items-center justify-center shrink-0">
                                                    <GramIcon size={16} className="opacity-70" />
                                                </div>
                                                <div className="min-w-0">
                                                    <p className="text-[13px] font-bold text-white/70 truncate">@{o.username}</p>
                                                    <div className="flex items-center gap-1.5">
                                                        <span className="text-[10px] font-bold text-white/40 uppercase">
                                                            {String(o.status || 'closed').toLowerCase()}
                                                            {o.expired_at ? ` · ${new Date(o.expired_at).toLocaleDateString()}` : ''}
                                                        </span>
                                                    </div>
                                                </div>
                                            </div>
                                            <div className="flex items-center gap-1 text-[12px] font-black text-white/55 shrink-0">
                                                {fmt(o.amount || o.price)} <GramIcon size={11} />
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            )}

            {/* ═════════════════════════════════════════════════════════
                MAIN TAB 3: ACTIVITY (Matching target screenshot exact UI)
            ═════════════════════════════════════════════════════════ */}
            {activeMainTab === 'activity' && (
                <div className="space-y-3">
                    <div>
                        <ExperimentalFilterBar
                            quickFind={searchQuery}
                            onQuickFindChange={(val) => setSearchQuery(val)}
                            viewMode={viewMode}
                            onViewModeChange={setViewMode}
                            items={activityLog}
                            filters={ACTIVITY_FILTERS}
                            activeFilter={activeFilter}
                            onFilterSelect={(id) => setActiveFilter(id)}
                            hideRoundTools={true}
                            onPurchasesClick={() => onNavigate('purchases')}
                        />
                    </div>

                    <div className="px-4">
                        {displayActivities.length === 0 ? (
                            <div className="flex flex-col items-center justify-center py-12 text-center">
                                <div className="w-24 h-24 mb-4 flex items-center justify-center">
                                    <CustomEmojiPlayer emojiId="108" size={96} />
                                </div>
                                <h3 className="text-sm font-medium text-white mb-1">It's empty so far</h3>
                            </div>
                        ) : (
                            <div className="bg-[#2c2c2e] rounded-3xl p-5 border border-white/[0.06] space-y-6">
                                {displayActivities.map(([dateGroup, items]) => (
                                    <div key={dateGroup}>
                                        <h3 className="text-xs font-bold text-[#8e8e93] tracking-wider uppercase mb-3">
                                            {dateGroup}
                                        </h3>
                                        <div className="space-y-4">
                                            {items.map((act: any) => (
                                                <div key={act.id} className="flex items-center justify-between group cursor-pointer hover:opacity-90 transition-opacity">
                                                    <div className="flex items-center gap-3.5">
                                                        {/* Avatar + Badge Icon */}
                                                        <div className="relative">
                                                            <div className="w-11 h-11 rounded-2xl bg-[#213527]/80 border border-emerald-500/20 flex items-center justify-center overflow-hidden text-2xl shadow-inner">
                                                                {act.emoji}
                                                            </div>
                                                            <div className="absolute -bottom-1 -right-1 w-5 h-5 rounded-full bg-emerald-500 border-2 border-[#2c2c2e] flex items-center justify-center text-black shadow-sm">
                                                                <ShoppingCart size={10} className="stroke-[2.5]" />
                                                            </div>
                                                        </div>

                                                        {/* Title & Action Subtitle */}
                                                        <div>
                                                            <h4 className="text-sm font-extrabold text-white tracking-tight">{act.title}</h4>
                                                            <p className="text-xs font-medium text-[#8e8e93] capitalize">{act.actionType}</p>
                                                        </div>
                                                    </div>

                                                    {/* Price Amount & Chevron Icon */}
                                                    <div className="flex items-center gap-2">
                                                        <span className={`text-sm font-extrabold tracking-tight ${act.isPositive ? 'text-emerald-400' : 'text-red-400'
                                                            }`}>
                                                            {act.amountDisplay}
                                                        </span>
                                                        <ChevronRight size={16} className="text-[#8e8e93]/60 group-hover:text-white transition-colors" />
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                </div>
            )}

            {/* ═════════════════════════════════════════════════════════
                MODALS
            ═════════════════════════════════════════════════════════ */}
            <AnimatePresence>
                {/* 1. ADD USERNAME MODAL */}
                {activeModal === 'add' && (
                    <div className="fixed inset-0 z-50 bg-black/80 backdrop-blur-md flex items-end sm:items-center justify-center p-4">
                        <motion.div initial={{ y: 50, opacity: 0 }} animate={{ y: 0, opacity: 1 }} exit={{ y: 50, opacity: 0 }} className="w-full max-w-md bg-[#1c1c1e] border border-white/10 rounded-3xl p-6">
                            <div className="flex items-center justify-between mb-4">
                                <h3 className="text-lg font-bold text-white flex items-center gap-2">
                                    <Plus size={20} className="text-cyan-400" /> {t('nav:addUsername')}
                                </h3>
                                <button onClick={() => setActiveModal(null)} className="text-white/50 hover:text-white"><X size={20} /></button>
                            </div>
                            <label className="text-xs text-white/50 font-bold uppercase tracking-wider mb-1 block">Username handle</label>
                            <input
                                type="text"
                                placeholder="@username"
                                value={formUsername}
                                onChange={(e) => setFormUsername(e.target.value)}
                                className="w-full px-4 py-3 rounded-2xl bg-white/5 border border-white/10 text-white font-bold outline-none mb-4 focus:border-cyan-400"
                            />
                            <button
                                onClick={handleAddAsset}
                                disabled={busyAction || !formUsername.trim()}
                                className="w-full py-3.5 rounded-2xl bg-gradient-to-r from-cyan-500 to-blue-500 text-white font-bold text-sm disabled:opacity-40"
                            >
                                {busyAction ? 'Adding...' : 'Add to Unlisted'}
                            </button>
                        </motion.div>
                    </div>
                )}

                {/* 2. SELL MODAL REMOVED (Replaced by PushMarketModal) */}
                {/* 3. CHANGE PRICE MODAL */}
                {activeModal === 'change_price' && (
                    <div className="fixed inset-0 z-50 bg-black/80 backdrop-blur-md flex items-end sm:items-center justify-center p-4">
                        <motion.div initial={{ y: 50, opacity: 0 }} animate={{ y: 0, opacity: 1 }} exit={{ y: 50, opacity: 0 }} className="w-full max-w-md bg-[#1c1c1e] border border-white/10 rounded-3xl p-6">
                            <div className="flex items-center justify-between mb-4">
                                <h3 className="text-lg font-bold text-white flex items-center gap-2">
                                    <Edit3 size={20} className="text-amber-400" /> Change Listing Price
                                </h3>
                                <button onClick={() => setActiveModal(null)} className="text-white/50 hover:text-white"><X size={20} /></button>
                            </div>
                            <label className="text-xs text-white/50 font-bold uppercase tracking-wider mb-1 block">{t('staking:newPriceGram')}</label>
                            <input
                                type="number"
                                placeholder="0.00"
                                value={formPrice}
                                onChange={(e) => setFormPrice(e.target.value)}
                                className="w-full px-4 py-3 rounded-2xl bg-white/5 border border-white/10 text-white font-bold outline-none mb-4 focus:border-amber-400"
                            />
                            <button
                                onClick={handleChangePrice}
                                disabled={busyAction || !formPrice}
                                className="w-full py-3.5 rounded-2xl bg-gradient-to-r from-amber-500 to-orange-500 text-white font-bold text-sm disabled:opacity-40"
                            >
                                {busyAction ? 'Updating...' : 'Update Price'}
                            </button>
                        </motion.div>
                    </div>
                )}

                {/* 4. CANCEL LISTING MODAL */}
                {activeModal === 'cancel' && (
                    <div className="fixed inset-0 z-50 bg-black/80 backdrop-blur-md flex items-end sm:items-center justify-center p-4">
                        <motion.div initial={{ y: 50, opacity: 0 }} animate={{ y: 0, opacity: 1 }} exit={{ y: 50, opacity: 0 }} className="w-full max-w-md bg-[#1c1c1e] border border-white/10 rounded-3xl p-6">
                            <div className="flex items-center justify-between mb-4">
                                <h3 className="text-lg font-bold text-white flex items-center gap-2 text-red-400">
                                    <XCircle size={20} /> {t('store:cancelListing')}
                                </h3>
                                <button onClick={() => setActiveModal(null)} className="text-white/50 hover:text-white"><X size={20} /></button>
                            </div>
                            <p className="text-xs text-white/60 mb-6">
                                Are you sure you want to cancel the listing for the selected item(s)? It will be moved back to your unlisted inventory.
                            </p>
                            <div className="flex gap-3">
                                <button onClick={() => setActiveModal(null)} className="flex-1 py-3.5 rounded-2xl bg-white/10 text-white font-bold text-sm">
                                    No, Keep
                                </button>
                                <button
                                    onClick={() => handleCancelListing()}
                                    disabled={busyAction}
                                    className="flex-1 py-3.5 rounded-2xl bg-red-500 text-white font-bold text-sm disabled:opacity-40"
                                >
                                    {busyAction ? 'Cancelling...' : 'Yes, Cancel'}
                                </button>
                            </div>
                        </motion.div>
                    </div>
                )}

                {/* 5. COUNTER OFFER MODAL */}
                {activeModal === 'counter' && (
                    <div className="fixed inset-0 z-50 bg-black/80 backdrop-blur-md flex items-end sm:items-center justify-center p-4">
                        <motion.div initial={{ y: 50, opacity: 0 }} animate={{ y: 0, opacity: 1 }} exit={{ y: 50, opacity: 0 }} className="w-full max-w-md bg-[#1c1c1e] border border-white/10 rounded-3xl p-6">
                            <div className="flex items-center justify-between mb-4">
                                <h3 className="text-lg font-bold text-white flex items-center gap-2 text-orange-400">
                                    <CornerDownLeft size={20} /> {t('marketplace:counterOffer')}
                                </h3>
                                <button onClick={() => setActiveModal(null)} className="text-white/50 hover:text-white"><X size={20} /></button>
                            </div>
                            <label className="text-xs text-white/50 font-bold uppercase tracking-wider mb-1 block">Counter Amount (GRAM)</label>
                            <input
                                type="number"
                                placeholder="0.00"
                                value={formCounterAmt}
                                onChange={(e) => setFormCounterAmt(e.target.value)}
                                className="w-full px-4 py-3 rounded-2xl bg-white/5 border border-white/10 text-white font-bold outline-none mb-4 focus:border-orange-400"
                            />
                            <button
                                onClick={handleCounterOffer}
                                disabled={busyAction || !formCounterAmt}
                                className="w-full py-3.5 rounded-2xl bg-gradient-to-r from-orange-500 to-amber-500 text-white font-bold text-sm disabled:opacity-40"
                            >
                                {busyAction ? 'Sending...' : 'Send Counter Offer'}
                            </button>
                        </motion.div>
                    </div>
                )}

                {/* 6. SEND MODAL */}
                {activeModal === 'send' && (
                    <div className="fixed inset-0 z-50 bg-black/80 backdrop-blur-md flex items-end sm:items-center justify-center p-4">
                        <motion.div initial={{ y: 50, opacity: 0 }} animate={{ y: 0, opacity: 1 }} exit={{ y: 50, opacity: 0 }} className="w-full max-w-md bg-[#1c1c1e] border border-white/10 rounded-3xl p-6">
                            <div className="flex items-center justify-between mb-4">
                                <h3 className="text-lg font-bold text-white flex items-center gap-2 text-blue-400">
                                    <Send size={20} /> Send Username
                                </h3>
                                <button onClick={() => setActiveModal(null)} className="text-white/50 hover:text-white"><X size={20} /></button>
                            </div>
                            <label className="text-xs text-white/50 font-bold uppercase tracking-wider mb-1 block">Recipient Telegram handle or ID</label>
                            <input
                                type="text"
                                placeholder="@recipient_username"
                                value={formRecipient}
                                onChange={(e) => setFormRecipient(e.target.value)}
                                className="w-full px-4 py-3 rounded-2xl bg-white/5 border border-white/10 text-white font-bold outline-none mb-4 focus:border-blue-400"
                            />
                            <button
                                onClick={handleSendUsername}
                                disabled={busyAction || !formRecipient.trim()}
                                className="w-full py-3.5 rounded-2xl bg-gradient-to-r from-blue-500 to-indigo-500 text-white font-bold text-sm disabled:opacity-40"
                            >
                                {busyAction ? 'Sending...' : 'Confirm Transfer'}
                            </button>
                        </motion.div>
                    </div>
                )}

                {/* 7. WITHDRAW MODAL */}
                {activeModal === 'withdraw' && (
                    <div className="fixed inset-0 z-50 bg-black/80 backdrop-blur-md flex items-end sm:items-center justify-center p-4">
                        <motion.div initial={{ y: 50, opacity: 0 }} animate={{ y: 0, opacity: 1 }} exit={{ y: 50, opacity: 0 }} className="w-full max-w-md bg-[#1c1c1e] border border-white/10 rounded-3xl p-6">
                            <div className="flex items-center justify-between mb-4">
                                <h3 className="text-lg font-bold text-white flex items-center gap-2 text-purple-400">
                                    <ArrowUp size={20} /> Withdraw Username / NFT
                                </h3>
                                <button onClick={() => setActiveModal(null)} className="text-white/50 hover:text-white"><X size={20} /></button>
                            </div>
                            <label className="text-xs text-white/50 font-bold uppercase tracking-wider mb-1 block">Destination Wallet Address</label>
                            <input
                                type="text"
                                placeholder="EQ..."
                                value={formAddress}
                                onChange={(e) => setFormAddress(e.target.value)}
                                className="w-full px-4 py-3 rounded-2xl bg-white/5 border border-white/10 text-white font-bold outline-none mb-4 focus:border-purple-400"
                            />
                            <button
                                onClick={handleWithdraw}
                                disabled={busyAction}
                                className="w-full py-3.5 rounded-2xl bg-gradient-to-r from-purple-500 to-pink-500 text-white font-bold text-sm disabled:opacity-40"
                            >
                                {busyAction ? 'Processing...' : 'Submit Withdrawal'}
                            </button>
                        </motion.div>
                    </div>
                )}
            </AnimatePresence>

            {/* Toast Banner */}
            <AnimatePresence>
                {toastMessage && (
                    <motion.div
                        initial={{ opacity: 0, y: 30 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: 30 }}
                        className="fixed bottom-20 left-1/2 -translate-x-1/2 z-50 px-5 py-3 rounded-2xl bg-black/90 border border-cyan-500/40 text-cyan-200 text-xs font-bold shadow-[0_0_25px_rgba(6,182,212,0.3)] backdrop-blur-xl"
                    >
                        {toastMessage}
                    </motion.div>
                )}
            </AnimatePresence>

            <PushMarketModal
                isOpen={!!pushMarketItem}
                onClose={() => setPushMarketItem(null)}
                item={pushMarketItem}
                onSuccess={() => loadData()}
            />
        </div>
    );
};

export default MyUser;
